// lineDiff.js
// ---------------------------------------------------------------------------
// Minimal line-based diff (LCS) for the AI Review modal's "online text
// comparison"-style JSON view. No external dependency - the classic dynamic-
// programming LCS is plenty fast for pretty-printed question JSON (hundreds
// of lines, not thousands).
// ---------------------------------------------------------------------------

/**
 * Diff two texts line-by-line.
 * Returns [{ type: 'same' | 'del' | 'add', text }] in display order.
 */
export const diffLines = (beforeText, afterText) => {
  const a = (beforeText || "").split("\n");
  const b = (afterText || "").split("\n");
  const n = a.length;
  const m = b.length;

  // dp[i][j] = LCS length of a[i..] vs b[j..]
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "same", text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: "del", text: a[i] });
      i++;
    } else {
      out.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", text: a[i++] });
  while (j < m) out.push({ type: "add", text: b[j++] });
  return out;
};

/**
 * Side-by-side ("compare mode") rows, like online diff tools' split view.
 * Pairs up deletion/addition runs so a modified line shows old-left /
 * new-right on the SAME row; a pure addition shows an empty left cell, so
 * it's immediately obvious WHERE content was inserted. Unchanged runs are
 * folded into { type: 'skip', count } markers with `context` lines kept.
 *
 * Row shapes:
 *   { type: 'same',   left, right }   identical line both sides
 *   { type: 'change', left, right }   modified line (red left, green right)
 *   { type: 'del',    left }          removed - right cell empty
 *   { type: 'add',    right }         added   - left cell empty
 *   { type: 'skip',   count }         folded unchanged run
 */
export const splitDiffRows = (beforeText, afterText, context = 2) => {
  const lines = diffLines(beforeText, afterText);

  // Fold distant unchanged lines first (same keep-window logic as unified).
  const keep = new Array(lines.length).fill(false);
  lines.forEach((l, idx) => {
    if (l.type !== "same") {
      const from = Math.max(0, idx - context);
      const to = Math.min(lines.length - 1, idx + context);
      for (let k = from; k <= to; k++) keep[k] = true;
    }
  });

  const rows = [];
  let skipped = 0;
  let delRun = [];
  let addRun = [];

  const flushRuns = () => {
    const paired = Math.min(delRun.length, addRun.length);
    for (let k = 0; k < paired; k++) {
      rows.push({ type: "change", left: delRun[k], right: addRun[k] });
    }
    for (let k = paired; k < delRun.length; k++) {
      rows.push({ type: "del", left: delRun[k] });
    }
    for (let k = paired; k < addRun.length; k++) {
      rows.push({ type: "add", right: addRun[k] });
    }
    delRun = [];
    addRun = [];
  };

  lines.forEach((l, idx) => {
    if (l.type === "del") {
      if (skipped > 0) { rows.push({ type: "skip", count: skipped }); skipped = 0; }
      delRun.push(l.text);
      return;
    }
    if (l.type === "add") {
      if (skipped > 0) { rows.push({ type: "skip", count: skipped }); skipped = 0; }
      addRun.push(l.text);
      return;
    }
    // same line - close any open change runs first
    flushRuns();
    if (!keep[idx]) {
      skipped++;
      return;
    }
    if (skipped > 0) { rows.push({ type: "skip", count: skipped }); skipped = 0; }
    rows.push({ type: "same", left: l.text, right: l.text });
  });
  flushRuns();
  if (skipped > 0) rows.push({ type: "skip", count: skipped });
  return rows;
};

/**
 * diffLines + collapsing: unchanged runs are folded into
 * { type: 'skip', count } markers, keeping `context` unchanged lines
 * around every change - the same presentation online diff tools use,
 * so a 200-line question with 3 edited lines stays a ~10-line view.
 */
export const unifiedDiffRows = (beforeText, afterText, context = 2) => {
  const lines = diffLines(beforeText, afterText);
  const keep = new Array(lines.length).fill(false);
  lines.forEach((l, idx) => {
    if (l.type !== "same") {
      const from = Math.max(0, idx - context);
      const to = Math.min(lines.length - 1, idx + context);
      for (let k = from; k <= to; k++) keep[k] = true;
    }
  });

  const rows = [];
  let skipped = 0;
  lines.forEach((l, idx) => {
    if (l.type === "same" && !keep[idx]) {
      skipped++;
      return;
    }
    if (skipped > 0) {
      rows.push({ type: "skip", count: skipped });
      skipped = 0;
    }
    rows.push(l);
  });
  if (skipped > 0) rows.push({ type: "skip", count: skipped });
  return rows;
};
